import { loadingState } from '../core/loadingState.js';
import AuthController from '../services/AuthController.js';
import { guardedFetch } from '../core/navGeneration.js';
import { failIfNotOk } from '../core/denialSurface.js';
import { openModal, installGuideHTML, embedFor } from './magnets-route.js';
import { EDITOR_SECTIONS, editorPath, getEditorSession, setEditorSession, clearEditorSession, takeEditorDraft, getSuspendedEditor, restoreSuspendedEditor } from '../core/magnetEditorSession.js';
import '../styles/magnet-editor.css';
import { magnetOverviewEnabled, magnetOrigin, magnetHomePath } from '../core/magnetOverview.js';

const scopeKey = () => JSON.stringify([AuthController.getStoredActiveOrg(), AuthController.getScope()?.activeOrg?.guid,
  AuthController.getCapabilities()]);

export async function mountEditor(container, guid, section = 'conversation') {
  // Navigo invokes programmatic handlers before updating the hash used by guardedFetch.
  await Promise.resolve();
  if (!Object.hasOwn(EDITOR_SECTIONS, section)) section = 'conversation';
  let scope = scopeKey();
  const actor = AuthController.getEntity()?.guid;
  const held = getSuspendedEditor();
  if (held && (held.guid !== guid || held.actor !== actor || held.org !== AuthController.getStoredActiveOrg())) {
    throw new Error('Return to the original account and workspace to recover your unfinished work.');
  }
  const current = getEditorSession();
  if (current?.guid === guid && current.actor === actor && current.scope === scope && current.element.isConnected) {
    current.path = editorPath(guid, section);
    current.controller.showSection(EDITOR_SECTIONS[section] || 'flow');
    return;
  }
  clearEditorSession();
  const host = document.createElement('div');
  host.className = 'page-shell mag-editor-host';
  host.innerHTML = loadingState('editor', 'Loading your editor');
  container.replaceChildren(host);
  container.scrollTop = 0;
  try {
    const response = await guardedFetch(`${AuthController.getConfig().BASE_URL}/m5t/v5/magnets/${encodeURIComponent(guid)}`, { cache: 'no-store' });
    const data = await response.json().catch(() => null);
    const updateUnavailable = 'The editor update is not available yet. Return to Your magnets and try again after the update.';
    if (response.status === 404 && data?.code !== 'magnet_not_found') throw new Error(updateUnavailable);
    failIfNotOk(response, data);
    if (!host.isConnected || scope !== scopeKey() || actor !== AuthController.getEntity()?.guid) return;
    if (data?.definition_read_version !== 1) throw new Error(updateUnavailable);
    const magnet = data.magnet;
    if (!magnet || magnet.guid !== guid) throw new Error('This magnet could not be confirmed. Return to Your magnets and try again.');
    if (!magnet.can_manage) throw new Error('You do not have permission to edit this magnet.');
    if (held) {
      if (getSuspendedEditor() !== held || held.actor !== AuthController.getEntity()?.guid || held.owner !== magnet.owner_guid) {
        throw new Error('This magnet’s account or ownership changed. Your unfinished work cannot be restored here.');
      }
      held.updateScope();
      restoreSuspendedEditor(container, held, { changed: held.revision !== magnet.revision || held.logoRevision !== magnet.logo_revision });
      held.controller.showSection(EDITOR_SECTIONS[section]);
      return;
    }
    host.replaceChildren();
    const controller = openModal(magnet, EDITOR_SECTIONS[section] || 'flow', {
      host, freshDraft: takeEditorDraft(guid),
      isCurrentScope: () => scope === scopeKey() && actor === AuthController.getEntity()?.guid,
      onClose: () => { setEditorSession(null); window.router.navigate(magnetOverviewEnabled() ? magnetOrigin(guid) : '/magnets'); },
      onSaved: result => {
        setEditorSession(null);
        if (result.draft) { mountEditor(container, guid, 'conversation'); return; }
        host.replaceChildren();
        const heading = document.createElement('h1'); heading.textContent = magnet.status === 'DISABLED' ? 'Changes saved' : 'Your magnet is published';
        const message = document.createElement('p'); message.textContent = magnet.status === 'DISABLED'
          ? 'This magnet is still disabled.' : 'Publishing and installing are separate steps. Install the magnet, then use Your magnets to check website activity.';
        const back = document.createElement('a'); back.href = '#/magnets'; back.textContent = 'Back to magnets';
        const edit = document.createElement('a'); edit.href = `#${editorPath(guid)}`; edit.textContent = 'Continue editing';
        const links = document.createElement('p'); links.append(back, document.createTextNode(' · '), edit);
        host.append(heading, message, links);
        if (magnetOverviewEnabled()) {
          back.href = `#${magnetOrigin(guid)}`; back.textContent = magnetOrigin(guid) === '/magnets' ? 'Back to magnets' : 'Back to magnet';
          const setup = document.createElement('a'); setup.href = `#${magnetHomePath(guid, 'website')}`; setup.textContent = 'Website setup & installation';
          host.append(setup);
        } else if (magnet.status !== 'DISABLED') {
          const snippet = document.createElement('pre'); snippet.className = 'mag-launch-snippet'; snippet.textContent = embedFor(guid);
          const copy = document.createElement('button'); copy.type = 'button'; copy.className = 'btn btn-secondary'; copy.textContent = 'Copy installation code';
          copy.addEventListener('click', async () => {
            try { await navigator.clipboard.writeText(embedFor(guid)); copy.textContent = 'Copied'; }
            catch { copy.textContent = 'Select and copy the code above'; }
          });
          const guide = document.createElement('div'); guide.innerHTML = installGuideHTML(guid); host.append(snippet, copy, guide);
        }
        container.scrollTop = 0; heading.tabIndex = -1; heading.focus();
      },
      onSection: tab => {
        const name = Object.keys(EDITOR_SECTIONS).find(key => EDITOR_SECTIONS[key] === tab) || 'conversation';
        const session = getEditorSession();
        if (session) session.path = editorPath(guid, name);
        history.replaceState(history.state, '', `#${editorPath(guid, name)}`);
      },
    });
    if (magnetOverviewEnabled() && magnetOrigin(guid) !== '/magnets') {
      const back = host.querySelector('[data-x="back"]'); if (back) back.textContent = '← Back to magnet';
    }
    const session = { guid, scope, actor, org: AuthController.getStoredActiveOrg(), owner: magnet.owner_guid,
      revision: magnet.revision, logoRevision: magnet.logo_revision, element: host, controller, path: editorPath(guid, section),
      updateScope() { scope = scopeKey(); session.scope = scope; },
    };
    setEditorSession(session);
  } catch (error) {
    if (!host.isConnected || scope !== scopeKey() || actor !== AuthController.getEntity()?.guid) return;
    const message = document.createElement('p');
    message.setAttribute('role', 'alert'); message.textContent = error.message || 'Could not load this magnet.';
    const back = document.createElement('a'); back.href = '#/magnets'; back.textContent = 'Back to magnets';
    host.replaceChildren(message, back);
  }
}
